import type { UpdateJobInput } from '@chukua/common';
import { Alert, Button, Table } from 'antd';
import { useState } from 'react';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { CommercialTermsForm } from './CommercialTermsForm';
import { JobAccessorialOverrideModal } from './JobAccessorialOverrideModal';
import type { JobAccessorialTerm, JobDetail } from '../../../jobs.api';
import { useCommercialTerms } from '../hooks/use-commercial-terms';

interface JobCommercialTermsTabProps {
  job: JobDetail;
  canManage: boolean;
  error: string | null;
  saving: boolean;
  onSave: (input: UpdateJobInput) => Promise<void>;
}

const UNIT_LABELS: Record<JobAccessorialTerm['unit'], string> = { per_hour: 'hours', per_day: 'days', per_event: 'per event' };

function freeQuantityText(term: JobAccessorialTerm): string {
  if (term.effectiveFreeQuantity === null) return '—';
  return `${term.effectiveFreeQuantity} ${UNIT_LABELS[term.unit]}`;
}

/** A job's agreed price, who pays what, PO and payment terms, plus the free waiting/detention copied from the customer's rates and their corrections (PRD → Job intake → Commercial terms; gitaumoses4/chukua#33). */
export function JobCommercialTermsTab({ job, canManage, error, saving, onSave }: JobCommercialTermsTabProps) {
  const { terms, overrideError, overrideFreeQuantity } = useCommercialTerms(job.id);
  const [editing, setEditing] = useState<JobAccessorialTerm | null>(null);

  async function submitOverride(freeQuantity: number, reason: string): Promise<boolean> {
    if (!editing) return false;
    const saved = await overrideFreeQuantity(editing.accessorialTypeCode, editing.accessorialTypeName, freeQuantity, reason);
    if (saved) setEditing(null);
    return saved;
  }

  return (
    <div className="flex flex-col gap-8">
      <CommercialTermsForm job={job} error={error} saving={saving} canManage={canManage} onSave={onSave} />
      <section className="flex flex-col gap-4">
        <h2 className="text-sm font-semibold text-fg">Free waiting and detention</h2>
        {overrideError && <Alert type="error" title={overrideError} showIcon />}
        {terms === null ? (
          <RowListSkeleton label="Loading accessorial terms" rows={2} />
        ) : (
          <Table<JobAccessorialTerm>
            rowKey="accessorialTypeCode"
            pagination={false}
            dataSource={terms}
            locale={{ emptyText: 'No accessorial rates applied to this job when it was created.' }}
            scroll={{ x: 'max-content' }}
            columns={[
              { title: 'Charge', dataIndex: 'accessorialTypeName' },
              { title: 'Free', render: (_, term) => freeQuantityText(term) },
              { title: 'From rates', render: (_, term) => (term.isOverridden ? `${term.originalFreeQuantity ?? '—'} (changed)` : 'As copied') },
              {
                title: '',
                render: (_, term) =>
                  term.originalFreeQuantity !== null && (
                    <Button size="small" disabled={!canManage} onClick={() => setEditing(term)}>
                      Override
                    </Button>
                  ),
              },
            ]}
          />
        )}
      </section>
      <JobAccessorialOverrideModal term={editing} onCancel={() => setEditing(null)} onSubmit={submitOverride} />
    </div>
  );
}
